// Pure page-selection logic for the POD package PDF — which photos land in the
// document, in what order, with what header. Kept free of jsPDF / DOM / network
// so it's unit-testable and is the single source of truth for POD inclusion.

import { hawbLabel } from "./hawbLabel";

/**
 * Build the ordered list of PDF pages ({ label, photoUrl }) for a job — one
 * per POD photo.
 *
 * Rules:
 *  - Airport stops and stops with no photo produce no page.
 *  - Per-package PODs are real captures: include them even when the stop was
 *    later marked no-show / "scheduled in error" (e.g. the guard tapped "no
 *    packages received"). Older single-photo jobs fall back to stop.podPhotoUrl.
 *  - Any photographed package that doesn't match a stop (missing/unknown/airport
 *    stopId) is still appended rather than silently dropped.
 * @param {object} job
 * @returns {{label: string, photoUrl: string}[]}
 */
export function buildPodPageList(job) {
  const stops = Array.isArray(job.stops) ? job.stops : [];
  const packages = Array.isArray(job.packages) ? job.packages : [];

  // Direct moves: the header reads as the route ("Diamonds Direct Raleigh
  // to Diamonds Direct Charlotte") — the POD documents the move from pickup
  // to drop off, not just the one stop the photo was taken at.
  const realStops = stops.filter((s) => s.type !== "airport");
  const directRoute = job.jobType === "direct" && realStops.length >= 2
    ? `${realStops[0].locationName || "Pickup"} to ${realStops[realStops.length - 1].locationName || "Drop off"}`
    : null;

  // Same POD-number extraction + natural sort the Malca worksheet uses, so the
  // PDF pages come out in the SAME order (per location, by POD #).
  const hawbOf = (p) => p?.hawbNumber || p?.hawb || p?.trackingNumber || p?.awb || p?.reference || "";
  const byHawb = (a, b) => String(hawbOf(a)).localeCompare(String(hawbOf(b)), undefined, { numeric: true, sensitivity: "base" });

  const pages = [];
  const includedPkgIds = new Set();
  for (const stop of stops) {
    if (stop.type === "airport") continue;
    const baseLabel = directRoute || stop.locationName || "Pickup";
    const stopPkgs = packages.filter((p) => p.stopId === stop.id && p.podPhotoUrl).sort(byHawb);
    if (stopPkgs.length > 0) {
      stopPkgs.forEach((p, i) => {
        // Always show the package's Receipt/Order/HAWB number when it
        // exists — single-package stops included. The "#N" fallback is
        // only needed to disambiguate multiple unnumbered packages.
        const num = p.hawbNumber || (stopPkgs.length > 1 ? `#${i + 1}` : "");
        const label = num ? `${baseLabel} — ${hawbLabel(job)} ${num}` : baseLabel;
        pages.push({ label, photoUrl: p.podPhotoUrl });
        if (p.id) includedPkgIds.add(p.id);
      });
    } else if (stop.podPhotoUrl && !stop.noShow) {
      // Stop-level fallback photo — but not for a stop the guard skipped:
      // a no-show stop with no package PODs contributes nothing.
      pages.push({ label: baseLabel, photoUrl: stop.podPhotoUrl });
    }
  }

  // Safety net: any package with a POD photo that wasn't matched to a stop
  // above still has real proof of delivery — never drop it.
  const orphanPkgs = packages.filter((p) => p.podPhotoUrl && (!p.id || !includedPkgIds.has(p.id))).sort(byHawb);
  orphanPkgs.forEach((p, i) => {
    const stop = stops.find((s) => s.id === p.stopId);
    const baseLabel = directRoute || stop?.locationName || "Package";
    const num = p.hawbNumber || (orphanPkgs.length > 1 ? `#${i + 1}` : "");
    const label = num ? `${baseLabel} — ${hawbLabel(job)} ${num}` : baseLabel;
    pages.push({ label, photoUrl: p.podPhotoUrl });
  });

  return pages;
}
